import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Stack,
  Button,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export type FilterType = "textbox" | "dropdown" | "radio" |"text" | "select" | "number" | "date";



export type FilterField = {
  label: string;
  key: string;
  type: FilterType;
 options?: string[] | { label: string; value: string | number }[];
  placeholder?: string;
};

type SearchDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterFields: FilterField[];
  onSearch: (filters: Record<string, string>) => void;
  onCustomerChange?: (selectedCustomerName: string) => void;
  onReset?: () => void;
  initialFilters?: Record<string, string>;
};

export const SearchDrawer: React.FC<SearchDrawerProps> = ({
  open,
  onClose,
  filterFields,
  onSearch,
  onCustomerChange,
  onReset,
}) => {
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    const resetValues: Record<string, string> = {};
    filterFields.forEach((field) => (resetValues[field.key] = ""));
    setFilterValues(resetValues);
    onReset?.();
  };
  

  const getTransformedFilters = () => {
    const transformed: Record<string, string> = {};
    filterFields.forEach((field) => {
      const value = filterValues[field.key];
      if (value) {
        const suffix = field.type === "textbox" ? ":lk" : ":eq";
        transformed[`${field.key}${suffix}`] = value;
      }
    });
    return transformed;
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box width={350} p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography>Start Your Search</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          {filterFields.map((field) => {
            const value = filterValues[field.key] || "";
            switch (field.type) {
              case "textbox":
                return (
                  <TextField
                    key={field.key}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    fullWidth
                    size="small"
                  />
                );

              case "dropdown":
                return (
                  <TextField
                    key={field.key}
                    select
                    fullWidth
                    size="small"
                    label={field.label}
                    value={value}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      handleChange(field.key, newValue);

                      // 👇 Trigger callback if it's customerName
                      if (field.key === "customerName" && onCustomerChange) {
                        onCustomerChange(newValue);
                      }
                    }}
                  >{field.options?.map((option) =>
  typeof option === "string" ? (
    <MenuItem key={option} value={option}>
      {option}
    </MenuItem>
  ) : (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  )
)}

                  </TextField>
                );

              case "radio":
                return (
                  <Box key={field.key}>
                    <Typography color="#737373" fontWeight={500}>
                      {field.label}
                    </Typography>
                    <RadioGroup
                      row
                      value={value}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                    >
                      {field.options?.map((opt) => {
  const key = typeof opt === "string" ? opt : `${opt.value}`;
  const value = typeof opt === "string" ? opt : `${opt.value}`;
  const label = typeof opt === "string" ? opt : opt.label;

  return (
    <FormControlLabel
      key={key}
      value={value}
      control={<Radio />}
      label={label}
    />
  );
})}

                       
                    </RadioGroup>
                  </Box>
                );

              default:
                return null;
            }
          })}
        </Stack>

       <Box mt={4} display="flex" justifyContent="flex-start" gap={1}>
  <Button
    variant="contained"
    onClick={() => onSearch(getTransformedFilters())}
    sx={{ background: "#2e5bd9" }}
  >
    Search
  </Button>

  <Button variant="outlined" onClick={handleReset}>
    Reset
  </Button>
</Box>

      </Box>
    </Drawer>
  );
};
